import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { 
  SparkleIcon, RefreshIcon, ArrowIcon, SaveIcon, BookIcon,
  StarIcon, HeartIcon, PlayIcon, CloseIcon, InfoIcon,
  InsightIcon, FeelIcon, NeedIcon, ObserveIcon, CheckIcon
} from './BespokeIcons';

/**
 * PracticeCards - A provocative flashcard experience for NVC practice
 * 
 * Features:
 * - AI-generated questions, challenges, and vibe practices
 * - Covers NVC principles, micro-expressions, belonging, and more
 * - Full-screen card experience with beautiful animations
 * - Refresh for new cards, print PDF, save to dashboard
 * - Mobile-first design
 */

// Card categories with their styling
const CARD_CATEGORIES = {
  question: {
    label: 'Question',
    bgGradient: 'from-blue-900/80 to-indigo-900/80',
    borderColor: 'border-blue-500/50',
    iconColor: '#60A5FA',
    Icon: InsightIcon
  },
  challenge: {
    label: 'Challenge',
    bgGradient: 'from-amber-900/80 to-orange-900/80',
    borderColor: 'border-amber-500/50',
    iconColor: '#FBBF24',
    Icon: StarIcon
  },
  vibeCheck: {
    label: 'Vibe Practice',
    bgGradient: 'from-emerald-900/80 to-teal-900/80',
    borderColor: 'border-emerald-500/50',
    iconColor: '#10B981',
    Icon: HeartIcon
  },
  scavengerHunt: {
    label: 'Scavenger Hunt',
    bgGradient: 'from-purple-900/80 to-rose-900/80',
    borderColor: 'border-purple-500/50',
    iconColor: '#A855F7',
    Icon: SparkleIcon
  },
  wisdom: {
    label: 'Wisdom Pearl',
    bgGradient: 'from-rose-900/80 to-pink-900/80',
    borderColor: 'border-rose-500/50',
    iconColor: '#F43F5E',
    Icon: BookIcon
  },
  microExpression: {
    label: 'Micro-Expression',
    bgGradient: 'from-cyan-900/80 to-blue-900/80',
    borderColor: 'border-cyan-500/50',
    iconColor: '#06B6D4',
    Icon: ObserveIcon
  }
};

// Chime audio context for delightful feedback
const playChime = (pitch = 1) => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioContext();
    
    const osc = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(440 * pitch, audioCtx.currentTime);
    
    gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);
    
    osc.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    osc.start();
    osc.stop(audioCtx.currentTime + 0.5);
  } catch (e) {
    // Audio not available
  }
};

// Single Practice Card Component (Full Screen)
const PracticeCard = ({ card, onNext, onPrevious, showNavigation, currentIndex, totalCards }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [revealed, setRevealed] = useState(false);
  
  useEffect(() => {
    setRevealed(false);
    setIsFlipped(false);
    setTimeout(() => setRevealed(true), 100);
  }, [card]);
  
  const category = CARD_CATEGORIES[card.category] || CARD_CATEGORIES.question;
  const CategoryIcon = category.Icon;
  
  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-500 overflow-y-auto ${
      revealed ? 'opacity-100' : 'opacity-0'
    }`}>
      {/* Background */}
      <div className={`fixed inset-0 bg-gradient-to-br ${category.bgGradient}`}>
        <div className="absolute inset-0 bg-black/40" />
        {/* Animated particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-white/10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>
      
      {/* Card Content - Scrollable on mobile */}
      <div className={`relative z-10 w-full max-w-2xl my-auto py-16 sm:py-4 transform transition-all duration-700 ${
        revealed ? 'scale-100 translate-y-0' : 'scale-95 translate-y-8'
      }`}>
        <Card className={`bg-black/60 backdrop-blur-xl border-2 ${category.borderColor} overflow-visible`}>
          <CardContent className="p-6 sm:p-8 md:p-12 text-center max-h-[70vh] sm:max-h-none overflow-y-auto">
            {/* Category Badge */}
            <div className="flex justify-center mb-6">
              <Badge className={`px-4 py-2 bg-white/10 border ${category.borderColor} text-white/90`}>
                <CategoryIcon size={16} color={category.iconColor} className="mr-2" />
                {category.label}
              </Badge>
            </div>
            
            {/* Main Content */}
            <div className="mb-8">
              <h2 
                className="text-2xl sm:text-3xl md:text-4xl font-light text-white leading-relaxed mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {card.title}
              </h2>
              
              <p className="text-lg sm:text-xl text-white/80 leading-relaxed">
                {card.content}
              </p>
              
              {/* Optional hint/reflection */}
              {card.hint && (
                <div className="mt-8 p-4 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-sm text-white/60 italic">
                    💡 {card.hint}
                  </p>
                </div>
              )}
            </div>
            
            {/* Card Counter */}
            <div className="text-white/40 text-sm mb-6">
              {currentIndex + 1} of {totalCards}
            </div>
            
            {/* Navigation */}
            {showNavigation && (
              <div className="flex items-center justify-center gap-4">
                <Button
                  onClick={() => { playChime(0.8); onPrevious(); }}
                  variant="ghost"
                  disabled={currentIndex === 0}
                  className="text-white/60 hover:text-white disabled:opacity-30"
                >
                  <ArrowIcon size={20} color="currentColor" direction="left" className="mr-2" />
                  Previous
                </Button>
                
                <Button
                  onClick={() => { playChime(1.2); onNext(); }}
                  className="bg-white/20 hover:bg-white/30 text-white px-8"
                >
                  {currentIndex === totalCards - 1 ? 'Complete' : 'Next Card'}
                  <ArrowIcon size={20} color="currentColor" direction="right" className="ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.5; }
          50% { transform: translateY(-20px) scale(1.1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// Sample cards for fallback (defined outside component)
const SAMPLE_CARDS = [
  {
    id: '1',
    category: 'question',
    title: 'The Mirror of Empathy',
    content: 'When was the last time you truly listened to understand, rather than to respond? What did you discover about the other person?',
    hint: 'Empathy begins with curiosity about another\'s inner world.'
  },
  {
    id: '2',
    category: 'microExpression',
    title: 'Reading the Unspoken',
    content: 'The next time you\'re in conversation, notice the micro-expressions that flash across the other person\'s face. What do their eyebrows, corners of their mouth, and eye movements tell you about their true feelings?',
    hint: 'Genuine emotions appear for only 1/25th of a second before we mask them.'
  },
  {
    id: '3',
    category: 'challenge',
    title: 'The Gratitude Bridge',
    content: 'Think of someone you\'ve been in conflict with recently. Can you identify ONE thing they did that actually met a need of yours, even if it was small?',
    hint: 'Finding the gold in others transforms how we see the world.'
  },
  {
    id: '4',
    category: 'vibeCheck',
    title: 'Body Wisdom Practice',
    content: 'Close your eyes. Take three deep breaths. Where in your body do you feel tension right now? What need might that tension be signaling?',
    hint: 'The body never lies - it holds our unspoken truths.'
  },
  {
    id: '5',
    category: 'scavengerHunt',
    title: 'Connection Quest',
    content: 'Today, find three strangers and offer them a genuine, non-transactional compliment. Notice how it changes the energy between you.',
    hint: 'Small acts of recognition can shift the field of belonging.'
  }
];

// Main Practice Cards Component
const PracticeCards = ({ 
  userId,
  userName,
  onComplete,
  onClose,
  onStartBelonging // Callback to start Belonging experience
}) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading'); // loading, cards, complete
  const [cards, setCards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  
  // Load practice cards from API
  const loadCards = useCallback(async () => {
    setIsLoading(true);
    setPhase('loading');
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      console.log('Fetching practice cards from:', backendUrl);
      
      // Create AbortController for timeout - allow 30s for LLM generation
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const response = await fetch(`${backendUrl}/api/practice-cards/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userId || 'anonymous',
          count: 5
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Received cards:', data.cards?.length);
        const receivedCards = data.cards || [];
        if (receivedCards.length > 0) {
          setCards(receivedCards);
          setCurrentCardIndex(0);
          setPhase('cards');
          playChime(1);
        } else {
          setCards([...SAMPLE_CARDS]);
          setCurrentCardIndex(0);
          setPhase('cards');
        }
      } else {
        console.log('API error, using fallback cards');
        setCards([...SAMPLE_CARDS]);
        setCurrentCardIndex(0);
        setPhase('cards');
      }
    } catch (error) {
      console.error('Failed to load practice cards:', error);
      setCards([...SAMPLE_CARDS]);
      setCurrentCardIndex(0);
      setPhase('cards');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);
  
  // Initial load
  useEffect(() => {
    loadCards();
  }, [loadCards]);
  
  // Navigate cards
  const goToNextCard = () => {
    if (currentCardIndex < cards.length - 1) {
      setCurrentCardIndex(prev => prev + 1);
    } else {
      setPhase('complete');
    }
  };
  
  const goToPreviousCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(prev => prev - 1);
    }
  };
  
  // Save current card to collection
  const saveCard = () => {
    const currentCard = cards[currentCardIndex];
    if (!savedCards.find(c => c.id === currentCard.id)) {
      setSavedCards(prev => [...prev, currentCard]);
      toast.success('Card saved to your collection!');
      playChime(1.5);
    }
  };
  
  // Download as PDF
  const downloadPDF = () => {
    const cardsToDownload = savedCards.length > 0 ? savedCards : cards;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Practice Cards - Belonging Sessions</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&display=swap');
          body {
            font-family: 'Playfair Display', Georgia, serif;
            max-width: 800px;
            margin: 40px auto;
            padding: 20px;
            color: #1a1a1a;
          }
          h1 { 
            text-align: center; 
            color: #F59E0B; 
            border-bottom: 2px solid #FCD34D; 
            padding-bottom: 15px;
            margin-bottom: 30px;
          }
          .card {
            page-break-inside: avoid;
            margin-bottom: 40px;
            padding: 30px;
            border: 2px solid #E5E7EB;
            border-radius: 16px;
            background: linear-gradient(135deg, #FAFAFA, #F5F5F5);
          }
          .category {
            display: inline-block;
            background: #F59E0B;
            color: white;
            padding: 6px 16px;
            border-radius: 20px;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 15px;
          }
          .title {
            font-size: 22px;
            color: #1F2937;
            margin-bottom: 15px;
          }
          .content {
            font-size: 16px;
            color: #4B5563;
            line-height: 1.8;
            margin-bottom: 15px;
          }
          .hint {
            font-style: italic;
            color: #9CA3AF;
            font-size: 14px;
            border-left: 3px solid #F59E0B;
            padding-left: 15px;
          }
          .footer {
            text-align: center;
            margin-top: 50px;
            color: #9CA3AF;
            font-size: 12px;
          }
        </style>
      </head>
      <body>
        <h1>🌟 Practice Cards</h1>
        <p style="text-align: center; color: #6B7280; margin-bottom: 40px;">
          From the Belonging Sessions - NVC Practice & Wisdom
        </p>
        ${cardsToDownload.map((card, idx) => `
          <div class="card">
            <div class="category">${CARD_CATEGORIES[card.category]?.label || card.category}</div>
            <h2 class="title">${card.title}</h2>
            <p class="content">${card.content}</p>
            ${card.hint ? `<p class="hint">💡 ${card.hint}</p>` : ''}
          </div>
        `).join('')}
        <div class="footer">
          <p>belongingsessions.com | Practice with Compassion</p>
        </div>
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
    toast.success('Opening print dialog...');
  };
  
  // Save to dashboard
  const saveToDashboard = async () => {
    if (!userId) {
      toast.error('Please sign in to save to your dashboard');
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/practice-cards/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          cards: savedCards.length > 0 ? savedCards : cards,
          savedAt: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        toast.success('Cards saved to your dashboard!');
      } else {
        toast.error('Failed to save. Please try again.');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save. Please try again.');
    }
  };
  
  // Loading messages that rotate
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const loadingMessages = [
    "Crafting your practice cards...",
    "Drawing wisdom from the field of belonging...",
    "Consulting the oracle of connection...",
    "Weaving questions that matter...",
    "Almost there... good things take time..."
  ];
  
  // Rotate loading messages
  useEffect(() => {
    if (phase === 'loading') {
      const interval = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [phase, loadingMessages.length]);
  
  // Loading state
  if (phase === 'loading') {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-zinc-900 via-purple-900/20 to-zinc-900 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          {/* Animated orb */}
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-500/30 to-purple-500/30 animate-pulse" />
            <div className="absolute inset-2 rounded-full bg-gradient-to-br from-amber-500/20 to-purple-500/20 animate-ping" style={{ animationDuration: '2s' }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <SparkleIcon size={32} color="#FBBF24" className="animate-pulse" />
            </div>
          </div>
          
          <p className="text-white/80 text-lg font-light transition-opacity duration-500">
            {loadingMessages[loadingMessageIndex]}
          </p>
          <p className="text-white/40 text-sm mt-3">
            ElchmE is generating unique cards just for you
          </p>
          
          {/* Progress dots */}
          <div className="flex justify-center gap-2 mt-6">
            {loadingMessages.map((_, idx) => (
              <div 
                key={idx}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === loadingMessageIndex ? 'bg-amber-400 scale-125' : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Cards view
  if (phase === 'cards' && cards.length > 0) {
    return (
      <div className="relative">
        <PracticeCard
          card={cards[currentCardIndex]}
          onNext={goToNextCard}
          onPrevious={goToPreviousCard}
          showNavigation={true}
          currentIndex={currentCardIndex}
          totalCards={cards.length}
        />
        
        {/* Top toolbar */}
        <div className="fixed top-4 left-4 right-4 z-[60] flex justify-between items-center">
          <Button
            onClick={onClose}
            variant="ghost"
            className="text-white/60 hover:text-white bg-black/30 backdrop-blur-sm"
          >
            <CloseIcon size={16} color="currentColor" className="mr-2" />
            Exit
          </Button>
          
          <div className="flex gap-2">
            <Button
              onClick={() => setShowInfo(true)}
              variant="ghost"
              className="text-white/60 hover:text-white bg-black/30 backdrop-blur-sm"
            >
              <InfoIcon size={16} color="currentColor" />
            </Button>
            
            <Button
              onClick={saveCard}
              variant="ghost"
              className="text-white/60 hover:text-white bg-black/30 backdrop-blur-sm"
            >
              <SaveIcon size={16} color="currentColor" className="mr-2" />
              Save
            </Button>
            
            <Button
              onClick={() => { loadCards(); playChime(0.6); }}
              variant="ghost"
              className="text-white/60 hover:text-white bg-black/30 backdrop-blur-sm"
            >
              <RefreshIcon size={16} color="currentColor" className="mr-2" />
              New Cards
            </Button>
          </div>
        </div>
        
        {/* Info Dialog */}
        <Dialog open={showInfo} onOpenChange={setShowInfo}>
          <DialogContent className="bg-zinc-900 border-amber-500/30 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-amber-400 flex items-center gap-2">
                <SparkleIcon size={20} color="#FBBF24" />
                About Practice Cards
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-white/80 text-sm">
              <p>
                Practice Cards are AI-generated prompts designed to deepen your understanding 
                of Nonviolent Communication and the art of belonging.
              </p>
              <p>
                Each card draws from the wisdom of the Belonging Sessions, including insights 
                about micro-expressions, empathy, the OFNR process, and connecting authentically.
              </p>
              <div className="grid grid-cols-2 gap-3 mt-4">
                {Object.entries(CARD_CATEGORIES).map(([key, cat]) => (
                  <div key={key} className="flex items-center gap-2 text-xs">
                    <cat.Icon size={14} color={cat.iconColor} />
                    <span>{cat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  // Completion view
  if (phase === 'complete') {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-zinc-900 via-amber-900/20 to-zinc-900 flex items-center justify-center p-6">
        <div className="text-center max-w-lg">
          <div className="relative w-24 h-24 mx-auto mb-8">
            <div className="absolute inset-0 bg-amber-500/30 rounded-full animate-ping" />
            <div className="absolute inset-0 bg-amber-500/20 rounded-full" />
            <div className="relative w-full h-full rounded-full bg-gradient-to-br from-amber-500/40 to-amber-600/20 border-2 border-amber-500/50 flex items-center justify-center">
              <CheckIcon size={40} color="#FBBF24" />
            </div>
          </div>
          
          <h1 className="text-3xl text-white font-light mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Practice Complete!
          </h1>
          
          <p className="text-white/60 mb-8">
            You've explored {cards.length} practice cards. 
            {savedCards.length > 0 && ` You saved ${savedCards.length} card${savedCards.length > 1 ? 's' : ''} to your collection.`}
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={downloadPDF}
              className="w-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 text-amber-300 border border-amber-500/30"
            >
              <ArrowIcon size={16} color="#FCD34D" direction="down" className="mr-2" />
              Download as PDF
            </Button>
            
            {userId && (
              <Button
                onClick={saveToDashboard}
                className="w-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 hover:from-emerald-500/30 hover:to-teal-500/30 text-emerald-300 border border-emerald-500/30"
              >
                <SaveIcon size={16} color="#10B981" className="mr-2" />
                Save to Dashboard
              </Button>
            )}
            
            <Button
              onClick={() => { loadCards(); playChime(0.6); }}
              className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20"
            >
              <RefreshIcon size={16} color="currentColor" className="mr-2" />
              Get More Cards
            </Button>
            
            <div className="pt-4 border-t border-white/10">
              <p className="text-white/40 text-sm mb-3">Ready to practice with others?</p>
              <Button
                onClick={onStartBelonging}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white"
              >
                <PlayIcon size={16} color="currentColor" className="mr-2" />
                Start Belonging Practice
              </Button>
            </div>
            
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full text-white/50 hover:text-white"
            >
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

export default PracticeCards;
